import React, { createContext, useState, useEffect, useContext, useCallback, useRef, useMemo } from "react";
import { authService } from "../apis/authService";
import {
  setAuthSessionActive,
  setAuthSessionFrozen,
} from "../apis/authRefreshInterceptor";
import {
  connectWebSocket,
  disconnectWebSocket,
} from "../services/websocketService";
import SessionExpiredModal from "../components/ui/session-expired-modal";
import {
  prefetchUserAudioFiles,
  clearAudioInventory,
} from "../hooks/useAudioCache";

const AuthContext = createContext(null);
const AUTH_BROADCAST_CHANNEL = "vbot-auth";

function resolveUserId(user) {
  if (!user) return null;
  const id = user.userId ?? user.userid ?? user.id ?? null;
  return id != null ? String(id) : null;
}

function broadcastAuthMessage(msg) {
  if (typeof BroadcastChannel === "undefined") return;
  try {
    const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    channel.postMessage(msg);
    channel.close();
  } catch {
    /* ignore */
  }
}

function hardRedirectToSignIn() {
  const url = `${window.location.origin}/signin?sessionExpired=1`;
  window.location.replace(url);
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionLogoutLoading, setSessionLogoutLoading] = useState(false);
  const handlingSessionExpiredRef = useRef(false);
  const currentUserIdRef = useRef(null);

  useEffect(() => {
    setAuthSessionActive(!!currentUser && !sessionExpiredOpen);
  }, [currentUser, sessionExpiredOpen]);

  const currentUserId = useMemo(() => resolveUserId(currentUser), [currentUser]);
  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  const freezeExpiredSession = useCallback((fromBroadcast = false) => {
    if (handlingSessionExpiredRef.current) return;
    handlingSessionExpiredRef.current = true;

    // Stop silent-refresh loops and live sockets immediately (kills lag).
    setAuthSessionFrozen(true);
    setAuthSessionActive(false);
    try {
      disconnectWebSocket();
    } catch {
      /* ignore */
    }

    setSessionExpiredOpen(true);

    if (!fromBroadcast) {
      broadcastAuthMessage({ type: "SESSION_EXPIRED" });
    }
  }, []);

  useEffect(() => {
    let alive = true;
    const bootstrapSession = async () => {
      setLoading(true);
      try {
        // skipAuthRefresh: own a single refresh below (interceptor would double-call).
        let me = await authService.getMe({ skipAuthRefresh: true });

        if (!me.success) {
          const refreshed = await authService.refreshToken();
          if (refreshed.success) {
            me = await authService.getMe({ skipAuthRefresh: true });
          }
        }

        if (alive) {
          setCurrentUser(me.success ? me.data : null);
        }
      } catch (err) {
        console.warn("Auth bootstrap error:", err);
        if (alive) setCurrentUser(null);
      } finally {
        if (alive) setLoading(false);
      }
    };

    bootstrapSession();
    return () => {
      alive = false;
    };
  }, []);

  // Warm Socket.IO as soon as session is known so handshake overlaps UI paint / NotificationProvider setup.
  useEffect(() => {
    if (!currentUser || loading || sessionExpiredOpen) return;
    let cancelled = false;
    const run = () => {
      if (!cancelled) connectWebSocket();
    };
    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(run, { timeout: 1200 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(id);
      };
    }
    const t = setTimeout(run, 0);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [currentUser, loading, sessionExpiredOpen]);

  // Map available S3 audio files right after login / session restore.
  // Do not clear on the initial null bootstrap — that would wipe sessionStorage
  // before getMe() restores the user.
  const prevUserIdRef = React.useRef(undefined);
  useEffect(() => {
    const prev = prevUserIdRef.current;
    prevUserIdRef.current = currentUserId;

    if (!currentUserId) {
      if (prev) {
        clearAudioInventory(prev);
      }
      return;
    }
    prefetchUserAudioFiles({ force: true, userId: currentUserId }).catch(() => {});
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUser || sessionExpiredOpen) return;

    const refreshTokenInterval = setInterval(async () => {
      if (document.visibilityState === "hidden") return;
      try {
        // Prefer silent keep-alive: if access token still works, skip rotation.
        // skipAuthRefresh so we don't refresh via interceptor then again below.
        const me = await authService.getMe({ skipAuthRefresh: true });
        if (me.success) return;

        const result = await authService.refreshToken();
        if (result?.success) return;
        // Don't treat offline / transient failures as logout.
        if (result?.networkError) return;

        const meAgain = await authService.getMe({ skipAuthRefresh: true });
        if (meAgain.success) return;

        window.dispatchEvent(new CustomEvent("auth:session-expired"));
      } catch {
        // Ignore interval errors; visibility handler / API interceptor will recover.
      }
    }, 55 * 60 * 1000);

    return () => clearInterval(refreshTokenInterval);
  }, [currentUser, sessionExpiredOpen]);

  // Refresh when tab becomes visible again (background timers get throttled)
  useEffect(() => {
    if (!currentUser || sessionExpiredOpen) return;

    let refreshing = false;
    let lastCheckAt = 0;
    const MIN_CHECK_GAP_MS = 60 * 1000;

    const onVisibility = async () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastCheckAt < MIN_CHECK_GAP_MS) return;
      if (refreshing) return;
      refreshing = true;
      lastCheckAt = now;
      try {
        const me = await authService.getMe({ skipAuthRefresh: true });
        if (me.success) return;

        const result = await authService.refreshToken();
        if (result?.success) return;
        if (result?.networkError) return;

        const meAgain = await authService.getMe({ skipAuthRefresh: true });
        if (meAgain.success) return;

        window.dispatchEvent(new CustomEvent("auth:session-expired"));
      } catch {
        // Network/tab issues — do not force session-expired UI.
      } finally {
        refreshing = false;
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
    };
  }, [currentUser, sessionExpiredOpen]);

  // Refresh failed — freeze work + show logout popup (do not auto-logout)
  useEffect(() => {
    const onSessionExpired = () => freezeExpiredSession(false);
    window.addEventListener("auth:session-expired", onSessionExpired);

    let channel = null;
    if (typeof BroadcastChannel !== "undefined") {
      try {
        channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
        channel.onmessage = (event) => {
          if (event?.data?.type === "SESSION_EXPIRED") {
            freezeExpiredSession(true);
          } else if (event?.data?.type === "SESSION_EXPIRED_LOGOUT") {
            hardRedirectToSignIn();
          }
        };
      } catch {
        channel = null;
      }
    }

    return () => {
      window.removeEventListener("auth:session-expired", onSessionExpired);
      try {
        channel?.close();
      } catch {
        /* ignore */
      }
    };
  }, [freezeExpiredSession]);

  const logout = useCallback(async () => {
    setLoading(true);
    const uid = currentUserIdRef.current;
    setCurrentUser(null);
    clearAudioInventory(uid);
    try {
      disconnectWebSocket();
    } catch {
      /* ignore */
    }
    await authService.logout();
    setLoading(false);
  }, []);

  const handleSessionExpiredLogout = useCallback(async () => {
    setSessionLogoutLoading(true);
    setAuthSessionFrozen(true);
    setAuthSessionActive(false);

    const uid = currentUserIdRef.current;
    setCurrentUser(null);
    clearAudioInventory(uid);
    try {
      disconnectWebSocket();
    } catch {
      /* ignore */
    }

    // Don't block on a dead session — logout API may hang.
    try {
      await Promise.race([
        authService.logout().catch(() => {}),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ]);
    } catch {
      /* ignore */
    }

    broadcastAuthMessage({ type: "SESSION_EXPIRED_LOGOUT" });
    // Hard navigation clears zombie React/WS state so sign-in is clean (no manual refresh).
    hardRedirectToSignIn();
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const result = await authService.getMe();
      if (result.success && result.data) {
        setAuthSessionFrozen(false);
        setCurrentUser(result.data);
        return true;
      }
      setCurrentUser(null);
      return false;
    } catch (err) {
      console.warn("Auth refresh error:", err);
      setCurrentUser(null);
      return false;
    }
  }, []);
  const googleAuthenticate = refreshMe; // backward-compatible alias

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authService.login(email, password);
      console.log("Login result:", result.data.email);
      if (result.success) {
        setAuthSessionFrozen(false);
        setSessionExpiredOpen(false);
        handlingSessionExpiredRef.current = false;
        setCurrentUser({
          email: result.data.email,
          // If name isn't available, use email as fallback
          name: result.data.name || email.split("@")[0],
          isAdmin: result.data.isAdmin || false,
          role: result.data.role,
          userid: result.data.userid,
        });
        return { success: true };
      } else {
        setError(result.message);
        return { success: false, message: result.message };
      }
    } catch (err) {
      const errorMessage = err.message || "An unexpected error occurred";
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (name, email, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authService.signup(name, email, password);
      console.log("Login result:", result.data.email);

      if (result.success) {
        setAuthSessionFrozen(false);
        setCurrentUser({
          email: result.data.email,
          name: result.data.name,
        });
        return { success: true };
      } else {
        setError(result.message);
        return { success: false, message: result.message };
      }
    } catch (err) {
      setError("An unexpected error occurred");
      return { success: false, message: "An unexpected error occurred" };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    googleAuthenticate,
    refreshMe,
    login,
    signup,
    logout,
    isAuthenticated: !!currentUser,
    sessionExpiredOpen,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionExpiredModal
        isOpen={sessionExpiredOpen}
        onLogout={handleSessionExpiredLogout}
        isLoggingOut={sessionLogoutLoading}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
